import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Mail,
  ArrowRight,
  MapPin,
  Phone,
  MessageSquare,
} from "lucide-react";

export default function Contact() {
  const navigate = useNavigate();

  function goToSection(id) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    try {
      navigate("/");
    } catch (e) {
      try {
        window.location.hash = "#/";
      } catch (err) {}
    }

    setTimeout(() => {
      const el2 = document.getElementById(id);
      if (el2) el2.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  }

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-36 bg-ink-surface overflow-hidden"
    >
      <div
        className="eco-blob bg-eco-light"
        style={{
          width: 500,
          height: 500,
          top: "20%",
          right: "-10%",
          opacity: 0.2,
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-start">

          {/* LEFT SIDE */}
          <div className="lg:col-span-7 reveal">
            <span className="overline">Get In Touch</span>

            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mt-5 leading-[1.05] tracking-tight">
              Let&apos;s build a
              <br />
              <span className="gradient-text-eco">cleaner India</span>, together.
            </h2>

            <p className="mt-6 text-white/65 text-base md:text-lg leading-relaxed max-w-xl">
              Whether you're a franchise partner, investor, fleet operator,
              land owner or clean-energy enthusiast — our team is ready to
              help you take the next step.
            </p>

            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/enquiry" className="btn-saffron">
                Start an Enquiry
                <ArrowRight className="w-4 h-4" />
              </Link>

              <button
                onClick={() => goToSection("opportunities")}
                className="btn-outline-green"
              >
                <MessageSquare className="w-4 h-4 text-eco-light" />
                View Opportunities
              </button>
            </div>

            {/* CONTACT INFO */}
            <div className="mt-12 grid sm:grid-cols-3 gap-6">
              {[
                {
                  icon: MapPin,
                  label: "Headquarters",
                  value:
                    "SCO 15, HSVP Complex, Sector 4-5, Karnal 132001",
                  action:
                    "https://www.google.com/maps/search/?api=1&query=SCO+15+HSVP+Complex+Sector+4-5+Karnal+132001",
                  type: "map",
                },
                {
                  icon: Phone,
                  label: "Phone",
                  value: "+91 8930225999",
                  action: "tel:+918930225999",
                  type: "phone",
                },
                {
                  icon: Mail,
                  label: "Email",
                  value: "contact@indianecofuels.co",
                  action: "mailto:contact@indianecofuels.co",
                  type: "email",
                },
              ].map((c) => (
                <a
                  key={c.label}
                  href={c.action}
                  target={c.type === "map" ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                  className="border-l border-eco-light/30 pl-5 hover:border-eco-light transition-colors group"
                >
                  <c.icon className="w-4 h-4 text-eco-light mb-3 group-hover:scale-110 transition-transform" />

                  <div className="font-mono-eco text-[10px] tracking-widest text-white/40 uppercase">
                    {c.label}
                  </div>

                  <div className="text-white/85 mt-1.5 text-sm leading-relaxed group-hover:text-eco-light transition-colors">
                    {c.value}
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="lg:col-span-5 reveal">
            <div className="glass-card p-8 md:p-10 grain relative overflow-hidden h-full">
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-eco-light/20 blur-3xl" />

              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-eco-light animate-pulse"></div>

                  <span className="font-mono-eco text-[11px] tracking-widest uppercase text-white/60">
                    INDIAN ECO FUELS
                  </span>
                </div>

                <h3 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                  Powering India's
                  <span className="gradient-text-eco"> Green Future</span>
                </h3>

                <p className="text-white/60 mt-5 leading-relaxed">
                  We are building a nationwide ecosystem of Bio-CNG, Biofuels,
                  Sustainable Energy Infrastructure and Clean Mobility Solutions.
                </p>

                <div className="mt-8 space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-eco-light/10 flex items-center justify-center">
                      🌱
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">
                        Sustainable Growth
                      </h4>
                      <p className="text-sm text-white/50 mt-1">
                        Promoting renewable fuel infrastructure across India.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-eco-light/10 flex items-center justify-center">
                      ⛽
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">
                        Fuel Retail Expansion
                      </h4>
                      <p className="text-sm text-white/50 mt-1">
                        Franchise and dealership opportunities for entrepreneurs.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-eco-light/10 flex items-center justify-center">
                      ⚡
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">
                        Clean Energy Network
                      </h4>
                      <p className="text-sm text-white/50 mt-1">
                        Supporting EV, Bio-CNG and future energy solutions.
                      </p>
                    </div>
                  </div>
                </div>

                {/* SUPPORT */}
                <div className="mt-10 pt-6 border-t border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-eco-light/10 flex items-center justify-center">
                      📞
                    </div>

                    <div>
                      <div className="text-sm text-white/50">
                        Need assistance? Our team is here to help.
                      </div>

                      <a
                        href="tel:+918045679000"
                        className="text-white font-mono-eco mt-1 block hover:text-eco-light transition-colors"
                      >
                        +91 8930225999                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/10 text-sm text-white/50">
                  SCO 15, HSVP Complex, Sector 4-5, Karnal 132001
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}